import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Loader2, Award, Save, FileDown, Eye } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { exportCertificateToPDF } from "@/lib/contractPdfExport";
import { saveDocumentToEmployeeFile, getDocumentCategory, getDocumentTypeName } from "@/lib/documentStorage";
import { PdfPreviewDialog } from "./PdfPreviewDialog";

interface Employee {
  id: string;
  name: string;
  position: string;
}

interface CompanySettings {
  name: string | null;
  street: string | null;
  house_number: string | null;
  postal_code: string | null;
  city: string | null;
}

export const CertificateForm = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [companySettings, setCompanySettings] = useState<CompanySettings | null>(null);
  const [previewDoc, setPreviewDoc] = useState<any>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  
  
  const [formData, setFormData] = useState({
    selectedEmployeeId: "",
    personName: "",
    certificateType: "training",
    certificateTitle: "",
    certificateContent: "",
    documentDate: new Date().toISOString().split("T")[0],
    issueDate: new Date().toISOString().split("T")[0],
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [employeesRes, companyRes] = await Promise.all([
        supabase.from("employees").select("id, name, position").is("archived_at", null),
        supabase.from("company_settings").select("name, street, house_number, postal_code, city").single()
      ]);

      if (employeesRes.data) setEmployees(employeesRes.data);
      if (companyRes.data) setCompanySettings(companyRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleEmployeeSelect = (employeeId: string) => {
    const employee = employees.find(e => e.id === employeeId);
    if (employee) {
      setFormData(prev => ({
        ...prev,
        selectedEmployeeId: employeeId,
        personName: employee.name,
      }));
    }
  };

  const handleSave = async () => {
    if (!user) return;
    if (!formData.personName || !formData.certificateTitle) {
      toast.error("Bitte füllen Sie alle Pflichtfelder aus");
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase.from("contract_templates").insert({
        template_type: "certificate",
        employee_id: formData.selectedEmployeeId || null,
        person_name: formData.personName,
        certificate_type: formData.certificateType,
        certificate_title: formData.certificateTitle,
        certificate_content: formData.certificateContent,
        issue_date: formData.issueDate,
        status: "draft",
        created_by: user.id,
      });

      if (error) throw error;

      // Create + archive PDF to employee documents (if employee selected)
      if (formData.selectedEmployeeId) {
        try {
          const pdfDoc = await exportCertificateToPDF(
            {
              personName: formData.personName,
              certificateType: formData.certificateType,
              certificateTitle: formData.certificateTitle,
              certificateContent: formData.certificateContent,
              documentDate: formData.documentDate,
              issueDate: formData.issueDate,
              companyName: companySettings?.name || "",
              companyAddress: companySettings
                ? `${companySettings.street || ""} ${companySettings.house_number || ""}, ${companySettings.postal_code || ""} ${companySettings.city || ""}`
                : "",
            },
            false
          );

          const category = getDocumentCategory("certificate");
          const typeName = getDocumentTypeName("certificate");
          const result = await saveDocumentToEmployeeFile(
            formData.selectedEmployeeId,
            `${typeName} - ${formData.certificateTitle}`,
            category,
            pdfDoc,
            user.id
          );

          if (result.success) {
            toast.success("Zertifikat gespeichert und in Dokumente abgelegt");
          } else {
            console.warn("Dokument konnte nicht gespeichert werden:", result.error);
            toast.error("Zertifikat gespeichert, PDF-Ablage fehlgeschlagen");
          }
        } catch (e) {
          console.error("Error archiving PDF:", e);
          toast.error("Zertifikat gespeichert, PDF-Ablage fehlgeschlagen");
        }
      } else {
        toast.success("Zertifikat wurde gespeichert");
      }
      resetForm();
    } catch (error) {
      console.error("Error saving certificate:", error);
      toast.error("Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const buildPdfPayload = () => ({
    personName: formData.personName,
    certificateType: formData.certificateType,
    certificateTitle: formData.certificateTitle,
    certificateContent: formData.certificateContent,
    documentDate: formData.documentDate,
    issueDate: formData.issueDate,
    companyName: companySettings?.name || "",
    companyAddress: companySettings ? `${companySettings.street || ""} ${companySettings.house_number || ""}, ${companySettings.postal_code || ""} ${companySettings.city || ""}` : "",
  });

  const handlePreview = async () => {
    if (!formData.personName || !formData.certificateTitle) {
      toast.error("Bitte füllen Sie Mitarbeiter und Titel aus");
      return;
    }
    try {
      const pdfDoc = await exportCertificateToPDF(buildPdfPayload(), false);
      setPreviewDoc(pdfDoc);
      setPreviewOpen(true);
    } catch (e) {
      console.error("Error generating preview:", e);
      toast.error("Fehler beim Erstellen der Vorschau");
    }
  };

  const handleExportPDF = async () => {
    if (!formData.personName || !formData.certificateTitle) {
      toast.error("Bitte füllen Sie alle Pflichtfelder aus");
      return;
    }

    try {
      // Pass saveToFile: false to get the PDF document without auto-downloading
      const pdfDoc = await exportCertificateToPDF({
        personName: formData.personName,
        certificateType: formData.certificateType,
        certificateTitle: formData.certificateTitle,
        certificateContent: formData.certificateContent,
        documentDate: formData.documentDate,
        issueDate: formData.issueDate,
        companyName: companySettings?.name || "",
        companyAddress: companySettings ? `${companySettings.street || ""} ${companySettings.house_number || ""}, ${companySettings.postal_code || ""} ${companySettings.city || ""}` : "",
      }, false);

      // Save to employee documents if employee is selected
      if (formData.selectedEmployeeId && user) {
        const category = getDocumentCategory('certificate');
        const typeName = getDocumentTypeName('certificate');
        const result = await saveDocumentToEmployeeFile(
          formData.selectedEmployeeId,
          `${typeName} - ${formData.certificateTitle}`,
          category,
          pdfDoc,
          user.id
        );
        if (result.success) {
          toast.success("PDF wurde erstellt und in den Mitarbeiter-Dokumenten gespeichert");
        } else {
          console.warn("Dokument konnte nicht gespeichert werden:", result.error);
        }
      }

      // Always download the PDF for the user
      pdfDoc.save(`${formData.certificateTitle.replace(/\s+/g, "_")}_${formData.personName.replace(/\s+/g, "_")}.pdf`);
      toast.success("PDF wurde erstellt");
    } catch (error) {
      console.error("Error exporting PDF:", error);
      toast.error("Fehler beim Erstellen des PDF");
    }
  };

  const resetForm = () => {
    setFormData({
      selectedEmployeeId: "",
      personName: "",
      certificateType: "training",
      certificateTitle: "",
      certificateContent: "",
      documentDate: new Date().toISOString().split("T")[0],
      issueDate: new Date().toISOString().split("T")[0],
    });
  };

  const certificateTemplates: Record<string, { title: string; content: string }> = {
    training: {
      title: "Schulungszertifikat",
      content: "hat erfolgreich an der Schulung teilgenommen und die erforderlichen Kenntnisse und Fähigkeiten nachgewiesen."
    },
    qualification: {
      title: "Qualifikationsnachweis",
      content: "hat die erforderliche Qualifikation erfolgreich erworben und ist berechtigt, die entsprechenden Tätigkeiten auszuführen."
    },
    work: {
      title: "Arbeitsbescheinigung",
      content: "war in unserem Unternehmen beschäftigt und hat die ihm übertragenen Aufgaben stets zu unserer vollsten Zufriedenheit erfüllt."
    },
  };

  const handleTypeChange = (type: string) => {
    const template = certificateTemplates[type];
    setFormData(prev => ({
      ...prev,
      certificateType: type,
      certificateTitle: template?.title || "",
      certificateContent: template?.content || "",
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Award className="h-5 w-5" />
          Zertifikat erstellen
        </CardTitle>
        <CardDescription>
          Erstellen Sie Schulungszertifikate, Qualifikationsnachweise oder Arbeitsbescheinigungen
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Employee Selection */}
        <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
          <h4 className="font-medium">Mitarbeiter auswählen</h4>
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label>Mitarbeiter (optional)</Label>
              <Select value={formData.selectedEmployeeId} onValueChange={handleEmployeeSelect}>
                <SelectTrigger>
                  <SelectValue placeholder="Mitarbeiter wählen..." />
                </SelectTrigger>
                <SelectContent>
                  {employees.map(emp => (
                    <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Name *</Label>
              <Input 
                value={formData.personName} 
                onChange={(e) => setFormData(prev => ({ ...prev, personName: e.target.value }))}
                placeholder="Name der Person"
              />
            </div>
          </div>
        </div>

        {/* Certificate Details */}
        <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
          <h4 className="font-medium">Zertifikat-Details</h4>
          <div className="grid gap-4 md:grid-cols-3">
            <div>
              <Label>Zertifikat-Typ</Label>
              <Select value={formData.certificateType} onValueChange={handleTypeChange}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="training">Schulungszertifikat</SelectItem>
                  <SelectItem value="qualification">Qualifikationsnachweis</SelectItem>
                  <SelectItem value="work">Arbeitsbescheinigung</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Dokumentendatum</Label>
              <Input 
                type="date"
                value={formData.documentDate} 
                onChange={(e) => setFormData(prev => ({ ...prev, documentDate: e.target.value }))}
              />
            </div>
            <div>
              <Label>Ausstellungsdatum *</Label>
              <Input 
                type="date"
                value={formData.issueDate} 
                onChange={(e) => setFormData(prev => ({ ...prev, issueDate: e.target.value }))}
              />
            </div>
          </div>

          <div>
            <Label>Titel *</Label>
            <Input 
              value={formData.certificateTitle} 
              onChange={(e) => setFormData(prev => ({ ...prev, certificateTitle: e.target.value }))}
              placeholder="z.B. Schulungszertifikat Arbeitssicherheit"
            />
          </div>

          <div>
            <Label>Inhalt / Beschreibung</Label>
            <Textarea 
              value={formData.certificateContent} 
              onChange={(e) => setFormData(prev => ({ ...prev, certificateContent: e.target.value }))}
              placeholder="Beschreibung der Qualifikation oder Schulung..."
              rows={4}
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-3">
          <Button variant="outline" onClick={handlePreview} disabled={saving} className="gap-2">
            <Eye className="h-4 w-4" />
            Vorschau
          </Button>
          <Button onClick={handleSave} disabled={saving} className="gap-2">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Speichern
          </Button>
          <Button variant="outline" onClick={handleExportPDF} className="gap-2">
            <FileDown className="h-4 w-4" />
            PDF exportieren
          </Button>
        </div>
      </CardContent>
      <PdfPreviewDialog
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        pdfDoc={previewDoc}
        title="Vorschau: Zertifikat"
        fileName={`Zertifikat_${formData.personName.replace(/\s+/g, "_") || "Vorschau"}.pdf`}
      />
    </Card>
  );
};
